import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * Splits the 4-button wallet grid (shop pay / apple pay / paypal / google pay) into two
 * rows on Safari, where the native grid layout renders incorrectly.
 *
 * This lives here (loaded unconditionally on every page) rather than as an inline script
 * inside `cart-summary.liquid`, because that snippet only renders once the cart has items.
 * When the cart goes from empty to non-empty, its markup is inserted via DOM morphing
 * (see `@theme/morph`), which parses new HTML with `DOMParser` — script elements parsed
 * that way never execute, even once moved into the live document. Setting up the
 * MutationObserver here, before the cart ever has items, avoids depending on that snippet's
 * script re-running.
 */
(function () {
  var ua = navigator.userAgent;
  var isSafari = /^((?!chrome|android).)*safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);

  if (!isSafari) return;

  var STYLE_ID = 'ctr-wallet-grid-split-style';
  var SPLIT_CLASS = 'wallet-cart-grid--ctr-split';
  var ROW_CLASS = 'ctr-wallet-row';

  function injectStyle(root) {
    if (root.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.' + SPLIT_CLASS + '{display:flex!important;flex-direction:column!important;}' +
      '.' + SPLIT_CLASS + '>.' + ROW_CLASS + '{display:flex!important;gap:0;}' +
      '.' + SPLIT_CLASS + '>.' + ROW_CLASS + '+.' + ROW_CLASS + '{margin-top:4px;}' +
      '.' + SPLIT_CLASS + '>.' + ROW_CLASS + '>.wallet-cart-button-container{flex:1 1 0;min-width:0;}';
    root.appendChild(style);
  }

  function splitGrid(grid) {
    if (!grid || grid.classList.contains(SPLIT_CLASS)) return;

    var buttons = Array.prototype.slice
      .call(grid.children)
      .filter(function (el) {
        return el.classList && el.classList.contains('wallet-cart-button-container');
      });

    if (buttons.length !== 4) return;

    var topRow = document.createElement('div');
    topRow.className = ROW_CLASS;
    var bottomRow = document.createElement('div');
    bottomRow.className = ROW_CLASS;

    buttons.slice(0, 2).forEach(function (button) {
      topRow.appendChild(button);
    });
    buttons.slice(2, 4).forEach(function (button) {
      bottomRow.appendChild(button);
    });

    grid.appendChild(topRow);
    grid.appendChild(bottomRow);
    grid.classList.add(SPLIT_CLASS);
  }

  function processShadowRoot(root) {
    injectStyle(root);
    splitGrid(root.querySelector('.wallet-cart-grid.accelerated-checkout-button-container'));

    if (root.__ctrWalletObserver) return;

    var observer = new MutationObserver(function () {
      splitGrid(root.querySelector('.wallet-cart-grid.accelerated-checkout-button-container'));
    });
    observer.observe(root, { childList: true, subtree: true });
    root.__ctrWalletObserver = observer;
  }

  function processElement(el) {
    if (el.__ctrWalletProcessed) return;

    if (el.shadowRoot) {
      el.__ctrWalletProcessed = true;
      processShadowRoot(el.shadowRoot);
      return;
    }

    var attempts = 0;
    var interval = setInterval(function () {
      attempts++;
      if (el.shadowRoot) {
        clearInterval(interval);
        el.__ctrWalletProcessed = true;
        processShadowRoot(el.shadowRoot);
      } else if (attempts > 40) {
        clearInterval(interval);
      }
    }, 250);
  }

  function scan() {
    document.querySelectorAll('shopify-accelerated-checkout-cart').forEach(processElement);
  }

  scan();
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
})();

/**
 * Drives two things on .cart-drawer__items-viewport based on the scroll position of the
 * inner .cart-drawer__items list:
 *
 * 1. [data-at-top] / [data-at-bottom] attributes, which toggle the CSS edge-fade
 *    (see cart-drawer.liquid).
 * 2. A custom scrollbar thumb (.cart-drawer__scrollbar-thumb), positioned via the
 *    --cart-drawer-scrollbar-thumb-top/-height CSS custom properties.
 *
 * Neither of these relies on the browser's native scrollbar or the shared `scroll-hint`
 * component (assets/scrolling.js):
 * - `scroll-hint` applies its fade via `mask-image` on the scrolling element itself, which
 *   also masks anything else painted in that box — including a native scrollbar.
 * - Native scrollbars are unreliable for "always visible": some browsers render them as an
 *   auto-hiding overlay with no fixed width/track, following an OS-level "show scroll bars"
 *   setting that page CSS can't override.
 *
 * So .cart-drawer__items hides its native scrollbar entirely (scrollbar-width: none +
 * ::-webkit-scrollbar { display: none }) and this script draws a fake one on a separate,
 * non-scrolling wrapper instead — always visible regardless of OS/browser scrollbar
 * preferences. It's presentational only (no drag-to-scroll), matching what the ticket asked
 * for (always-visible indicator, not a fully interactive scrollbar).
 */
(function () {
  var VIEWPORT_SELECTOR = '.cart-drawer__items-viewport';
  var ITEMS_SELECTOR = '.cart-drawer__items';

  function update(viewport, itemsEl) {
    var atTop = itemsEl.scrollTop <= 0;
    var atBottom = itemsEl.scrollTop + itemsEl.clientHeight >= itemsEl.scrollHeight - 1;

    viewport.toggleAttribute('data-at-top', atTop);
    viewport.toggleAttribute('data-at-bottom', atBottom);

    var scrollable = itemsEl.scrollHeight > itemsEl.clientHeight + 1;
    viewport.toggleAttribute('data-scrollable', scrollable);

    if (!scrollable) return;

    var thumbHeightPercent = (itemsEl.clientHeight / itemsEl.scrollHeight) * 100;
    var maxScrollTop = itemsEl.scrollHeight - itemsEl.clientHeight;
    var scrollRatio = maxScrollTop > 0 ? itemsEl.scrollTop / maxScrollTop : 0;
    var thumbTopPercent = (100 - thumbHeightPercent) * scrollRatio;

    viewport.style.setProperty('--cart-drawer-scrollbar-thumb-height', thumbHeightPercent + '%');
    viewport.style.setProperty('--cart-drawer-scrollbar-thumb-top', thumbTopPercent + '%');
  }

  function attach(viewport) {
    if (viewport.__ctrFadeAttached) return;

    var itemsEl = viewport.querySelector(ITEMS_SELECTOR);
    if (!itemsEl) return;

    viewport.__ctrFadeAttached = true;

    var onUpdate = function () {
      update(viewport, itemsEl);
    };

    onUpdate();
    itemsEl.addEventListener('scroll', onUpdate, { passive: true });
    new ResizeObserver(onUpdate).observe(itemsEl);
  }

  function scan() {
    document.querySelectorAll(VIEWPORT_SELECTOR).forEach(attach);
  }

  scan();
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
})();
